// Truck model for the road shipment form.
//
// A road file can move on several trucks. Every truck carries its own plate, trailer,
// driver, CMR / seal, buying rate and dates; every cargo line lists how much of it is
// loaded on each truck in `allocations`. With one truck the cargo line is edited directly
// and its single allocation mirrors it; with more trucks the per-truck loads are entered
// and the cargo line keeps their totals, because totals, RMBL, the dashboards and invoices
// still read cargoDetails. The file-level truckType / plateContainerNumber / cmrNumber /
// sealNumber / buyingRate are written from the trucks for the same reason.

export const LOAD_FIELDS = ["unitQuantity", "grossWeight", "cbm", "ldm", "chargeableWeight"];

export const TRUCK_TYPE_OPTIONS = [
    "Standard Truck",
    "Refrigerated Truck",
    "Container Truck",
    "Flatbed Truck",
    "Tanker Truck",
    "Lowboy Truck",
    "Dump Truck",
    "Car Carrier Truck",
    "Tow Truck",
    "Fire Truck",
].map((label) => ({ label }));

let truckSeq = 0;
export const newTruckId = () => `truck-${Date.now()}-${(truckSeq += 1)}`;

export const emptyTruck = (overrides = {}) => ({
    truckId: newTruckId(),
    truckType: "",
    plate: "",
    trailerPlate: "",
    trailerType: "",
    driverName: "",
    driverPhone: "",
    carrier: "",
    cmrNumber: "",
    sealNumber: "",
    buyingRate: "",
    pickupDate: null,
    pickupTime: "",
    estimatedDeliveryDate: null,
    estimatedDeliveryTime: "",
    actualDeliveryDate: null,
    actualDeliveryTime: "",
    notes: "",
    ...overrides,
});

const toDate = (value) => (value ? new Date(value) : null);

// Duplicate keeps the job (type, carrier, rate, dates); the vehicle and its documents are
// new, and its load starts empty.
export const duplicateTruck = (truck) =>
    emptyTruck({
        truckType: truck.truckType,
        trailerType: truck.trailerType,
        carrier: truck.carrier,
        buyingRate: truck.buyingRate,
        pickupDate: truck.pickupDate,
        pickupTime: truck.pickupTime,
        estimatedDeliveryDate: truck.estimatedDeliveryDate,
        estimatedDeliveryTime: truck.estimatedDeliveryTime,
        actualDeliveryDate: truck.actualDeliveryDate,
        actualDeliveryTime: truck.actualDeliveryTime,
        notes: truck.notes,
    });

// Files saved before multi-truck have no `trucks`; their single truck is the file-level fields.
export const trucksFromSaved = (saved = {}) => {
    if (Array.isArray(saved.trucks) && saved.trucks.length) {
        return saved.trucks.map((truck) =>
            emptyTruck({
                ...truck,
                truckId: truck.truckId || newTruckId(),
                buyingRate: truck.buyingRate ?? "",
                pickupDate: toDate(truck.pickupDate),
                estimatedDeliveryDate: toDate(truck.estimatedDeliveryDate),
                actualDeliveryDate: toDate(truck.actualDeliveryDate),
            })
        );
    }

    // A stable id, so corrections made elsewhere (the RMBL page, the migration script) find it.
    const savedId = saved._id || saved.id;
    return [
        emptyTruck({
            ...(savedId ? { truckId: `truck-${savedId}-1` } : {}),
            truckType: saved.truckType || "",
            plate: saved.plateContainerNumber || "",
            cmrNumber: saved.cmrNumber || "",
            // The form used to default the seal to 0.
            sealNumber: saved.sealNumber && String(saved.sealNumber) !== "0" ? String(saved.sealNumber) : "",
            buyingRate: saved.buyingRate ?? "",
            pickupDate: toDate(saved.pickupDate),
            pickupTime: saved.pickupTime || "",
            estimatedDeliveryDate: toDate(saved.estimatedDeliveryDate),
            estimatedDeliveryTime: saved.estimatedDeliveryTime || "",
            actualDeliveryDate: toDate(saved.actualDelivery),
            actualDeliveryTime: saved.actualDeliveryTime || "",
        }),
    ];
};

const num = (value) => Number(value) || 0;

const loadOf = (source = {}) =>
    LOAD_FIELDS.reduce((acc, field) => ({ ...acc, [field]: num(source[field]) }), {});

const sumLoads = (allocations) =>
    LOAD_FIELDS.reduce(
        (acc, field) => ({ ...acc, [field]: allocations.reduce((sum, a) => sum + num(a[field]), 0) }),
        {}
    );

// One allocation per truck, in truck order. With one truck it mirrors the cargo line; with
// more the cargo line is the sum of its allocations. A cargo saved before multi-truck has no
// allocations, so all of it sits on the first truck.
export const normalizeCargo = (cargo, trucks) => {
    if (!trucks.length) return { ...cargo, allocations: [] };

    if (trucks.length === 1) {
        return { ...cargo, allocations: [{ truckId: trucks[0].truckId, ...loadOf(cargo) }] };
    }

    const saved = Array.isArray(cargo.allocations) ? cargo.allocations : [];
    const known = saved.filter((a) => trucks.some((t) => t.truckId === a.truckId));
    const allocations = known.length
        ? trucks.map((truck) => ({
            truckId: truck.truckId,
            ...loadOf(saved.find((a) => a.truckId === truck.truckId)),
        }))
        : trucks.map((truck, i) => ({ truckId: truck.truckId, ...loadOf(i === 0 ? cargo : {}) }));

    return { ...cargo, ...sumLoads(allocations), allocations };
};

export const normalizeCargoList = (cargoDetails, trucks) =>
    (cargoDetails || []).map((cargo) => normalizeCargo(cargo, trucks));

export const setAllocation = (cargo, truckId, field, value, trucks) =>
    normalizeCargo(
        {
            ...cargo,
            allocations: (cargo.allocations || []).map((a) =>
                a.truckId === truckId ? { ...a, [field]: num(value) } : a
            ),
        },
        trucks
    );

// Splits `total` by `weights` so the shares always add up to `total`. Package counts stay
// whole numbers, their leftover units going one each to the largest fractions; for measures
// the rounding remainder goes to the last share.
const splitByWeights = (total, weights, whole) => {
    const sum = weights.reduce((acc, w) => acc + w, 0);
    const exact = weights.map((w) => (sum > 0 ? (total * w) / sum : total / weights.length));

    if (whole) {
        const shares = exact.map(Math.floor);
        let rest = Math.round(total - shares.reduce((acc, v) => acc + v, 0));
        exact
            .map((v, i) => [v - Math.floor(v), i])
            .sort((a, b) => b[0] - a[0] || a[1] - b[1])
            .forEach(([, i]) => {
                if (rest > 0) {
                    shares[i] += 1;
                    rest -= 1;
                }
            });
        return shares;
    }

    const shares = exact.map((v) => Math.round(v * 1000) / 1000);
    const rest = total - shares.reduce((acc, v) => acc + v, 0);
    shares[shares.length - 1] = Math.round((shares[shares.length - 1] + rest) * 1000) / 1000;
    return shares;
};

// A cargo total typed on the cargo line. With more trucks it is spread over them in their
// current proportion, or evenly while the current shares are an even split (or empty), so a
// value typed digit by digit is not skewed by the rounding of its first digits.
export const setCargoTotal = (cargo, field, value, trucks) => {
    if (trucks.length < 2 || !LOAD_FIELDS.includes(field)) {
        return normalizeCargo({ ...cargo, [field]: value }, trucks);
    }
    const current = normalizeCargo(cargo, trucks);
    const whole = field === "unitQuantity";
    const shares = current.allocations.map((a) => num(a[field]));
    const ones = shares.map(() => 1);
    const evenNow = splitByWeights(shares.reduce((acc, v) => acc + v, 0), ones, whole);
    const isEven = shares.every((v, i) => Math.abs(v - evenNow[i]) < 0.0005);
    const newShares = splitByWeights(num(value), isEven ? ones : shares, whole);
    return normalizeCargo(
        { ...current, allocations: current.allocations.map((a, i) => ({ ...a, [field]: newShares[i] })) },
        trucks
    );
};

// Splits the cargo line evenly over the trucks; package counts stay whole numbers.
export const splitCargoEvenly = (cargo, trucks) => {
    const n = trucks.length;
    const allocations = trucks.map((truck, i) => {
        const load = { truckId: truck.truckId };
        LOAD_FIELDS.forEach((field) => {
            const total = num(cargo[field]);
            if (field === "unitQuantity") {
                const base = Math.floor(total / n);
                load[field] = base + (i < total - base * n ? 1 : 0);
            } else {
                const share = Math.round((total / n) * 1000) / 1000;
                load[field] = i === n - 1 ? Math.round((total - share * (n - 1)) * 1000) / 1000 : share;
            }
        });
        return load;
    });
    return normalizeCargo({ ...cargo, allocations }, trucks);
};

// Removing a truck either moves its load onto `targetId` or drops it with the truck.
export const removeTruckFromCargo = (cargoDetails, trucks, truckId, targetId = null) => {
    const remaining = trucks.filter((t) => t.truckId !== truckId);

    return (cargoDetails || []).map((cargo) => {
        const current = normalizeCargo(cargo, trucks);
        const removed = current.allocations.find((a) => a.truckId === truckId) || loadOf();
        const allocations = current.allocations
            .filter((a) => a.truckId !== truckId)
            .map((a) =>
                a.truckId === targetId
                    ? LOAD_FIELDS.reduce((acc, f) => ({ ...acc, [f]: num(a[f]) + num(removed[f]) }), { truckId: a.truckId })
                    : a
            );
        // Totals first, so the one-truck mirror keeps what is left rather than the old line.
        return normalizeCargo({ ...current, ...sumLoads(allocations), allocations }, remaining);
    });
};

export const truckLoadTotals = (cargoDetails, trucks, truckId) =>
    sumLoads(
        normalizeCargoList(cargoDetails, trucks).map(
            (cargo) => cargo.allocations.find((a) => a.truckId === truckId) || {}
        )
    );

export const truckHasLoad = (cargoDetails, trucks, truckId) => {
    const load = truckLoadTotals(cargoDetails, trucks, truckId);
    return LOAD_FIELDS.some((f) => load[f] > 0);
};

export const truckLabel = (truck, index) => truck?.plate?.trim() || `Truck ${index + 1}`;

export const vehicleText = (truck) =>
    [truck.plate, truck.trailerPlate].map((v) => (v || "").trim()).filter(Boolean).join(" / ");

export const totalBuyingRate = (trucks) =>
    Math.round(trucks.reduce((sum, t) => sum + num(t.buyingRate), 0) * 100) / 100;

// Payload fields: the trucks themselves, plus the file-level fields older readers use
// (truck type of the first truck, every plate, total buying rate).
export const trucksForPayload = (trucks) => {
    const first = trucks[0] || emptyTruck();
    const joined = (key) =>
        [...new Set(trucks.map((t) => String(t[key] || "").trim()).filter(Boolean))].join(", ");
    const hasRate = trucks.some((t) => t.buyingRate !== "" && t.buyingRate != null);

    return {
        trucks: trucks.map((t) => ({ ...t, buyingRate: num(t.buyingRate) })),
        truckType: first.truckType || "",
        plateContainerNumber: trucks.map(vehicleText).filter(Boolean).join(", "),
        cmrNumber: joined("cmrNumber"),
        sealNumber: joined("sealNumber"),
        buyingRate: hasRate ? String(totalBuyingRate(trucks)) : "",
    };
};

// One CMR per truck: that truck's vehicle, documents and dates, and only the cargo it carries.
export const cmrPayloadsForTrucks = (shipmentData) => {
    const trucks = Array.isArray(shipmentData.trucks) ? shipmentData.trucks : [];
    const forTruck = (truck, i, cargoDetails) => ({
        ...shipmentData,
        cargoDetails,
        plateContainerNumber: vehicleText(truck),
        cmrNumber:
            truck.cmrNumber ||
            (trucks.length > 1 ? `${shipmentData.shipmentNo || "CMR"}-${i + 1}` : shipmentData.shipmentNo || ""),
        sealNumber: truck.sealNumber || "",
        actualDeparture: truck.pickupDate || shipmentData.actualDeparture,
    });

    if (trucks.length <= 1) {
        return [trucks[0] ? forTruck(trucks[0], 0, shipmentData.cargoDetails || []) : shipmentData];
    }

    const payloads = trucks
        .map((truck, i) => {
            const cargoDetails = (shipmentData.cargoDetails || [])
                .map((cargo) => {
                    const a = (cargo.allocations || []).find((x) => x.truckId === truck.truckId);
                    return a ? { ...cargo, ...loadOf(a) } : null;
                })
                .filter((cargo) => cargo && LOAD_FIELDS.some((f) => cargo[f] > 0));
            return forTruck(truck, i, cargoDetails);
        })
        // A truck with nothing loaded yet still gets its CMR when no truck has any load.
        .filter((payload, _i, all) => payload.cargoDetails.length || all.every((p) => !p.cargoDetails.length));

    return payloads;
};
